import React, { useState } from "react";
import { Lock } from "lucide-react";
import MagicalCard from "./MagicalCard";
import { Icon, Modal, EmptyState } from "./common";
import { useLang } from "../i18n";
import { fmtDate, wsLabel } from "../utils";
import { useConfig } from "../context/ConfigContext";

export default function BadgesGrid({ badges = [], asCard = true, testId = "badges-grid" }) {
  const { t, pick, lang } = useLang();
  const { config } = useConfig();
  const [selected, setSelected] = useState(null);
  const cats = config?.categories || [];

  const grid = (
    <div className="grid grid-cols-3 gap-3">
      {badges.map((b) => (
        <button key={b.key} data-testid={`badge-${b.key}`} onClick={() => setSelected(b)}
          className={`flex flex-col items-center gap-1.5 rounded-2xl p-3 border transition-all duration-300 hover:-translate-y-1 ${
            b.earned ? "border-transparent" : "border-white/8 bg-[#0D0B38]/40"}`}
          style={b.earned ? { background: `linear-gradient(165deg, ${b.color}2E, ${b.color}0A)`, borderColor: `${b.color}55` } : {}}>
          <div className={`relative w-14 h-14 rounded-full flex items-center justify-center ${b.earned ? "" : "grayscale opacity-45"}`}
            style={b.earned ? { background: `linear-gradient(135deg, ${b.color}, ${b.color}99)`, boxShadow: `0 0 16px ${b.color}66` } : { background: "rgba(255,255,255,0.07)" }}>
            <Icon name={b.icon} className={`w-7 h-7 ${b.earned ? "text-white" : "text-[#A5A0D6]"}`} />
            {!b.earned && (
              <span className="absolute -bottom-1 -end-1 w-5 h-5 rounded-full bg-[#171347] border border-white/20 flex items-center justify-center">
                <Lock className="w-3 h-3 text-[#A5A0D6]" />
              </span>
            )}
          </div>
          <span className={`text-[11px] font-extrabold text-center leading-tight ${b.earned ? "text-white" : "text-[#A5A0D6]"}`}>
            {pick(b, "name")}
          </span>
        </button>
      ))}
    </div>
  );

  const autoCat = selected?.auto?.category_key ? cats.find((c) => c.key === selected.auto.category_key) : null;

  return (
    <>
      {asCard ? (
        <MagicalCard title={wsLabel(config?.ui_labels, "badges", lang, t("dash.badges"))} variant="ribbon-cyan" testId={testId}>
          {badges.length === 0 ? <EmptyState icon="Award" title={t("badges_page.empty")} testId="badges-empty" /> : grid}
        </MagicalCard>
      ) : grid}
      <Modal open={!!selected} onClose={() => setSelected(null)} testId="badge-modal">
        {selected && (
          <div className="flex flex-col items-center text-center gap-3">
            <div className={`w-24 h-24 rounded-full flex items-center justify-center ${selected.earned ? "" : "grayscale opacity-60"}`}
              style={{ background: `linear-gradient(135deg, ${selected.color}, ${selected.color}88)`, boxShadow: `0 0 30px ${selected.color}77` }}>
              <Icon name={selected.icon} className="w-12 h-12 text-white" />
            </div>
            <h4 className="text-2xl font-black">{pick(selected, "name")}</h4>
            <p className="text-sm text-[#D8D4F2] font-semibold">{pick(selected, "desc")}</p>
            {selected.earned ? (
              <p className="text-xs font-bold text-[#48D276] bg-[#48D276]/12 border border-[#48D276]/30 rounded-full px-4 py-1.5">
                {t("badges_page.earned_on", { date: fmtDate(selected.earned_at, lang) })}
              </p>
            ) : (
              <div className="text-xs font-bold text-[#FFC83D] bg-[#FFC83D]/10 border border-[#FFC83D]/30 rounded-2xl px-4 py-2.5">
                <p className="mb-1 text-[#A5A0D6]">{t("badges_page.how_to_unlock")}</p>
                {autoCat
                  ? t("badges_page.auto_rule", { count: selected.auto.count, category: pick(autoCat, "name") })
                  : t("badges_page.manual")}
              </div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}
